import ShapeBase, { DEFAULT_MARGIN } from './ShapeBase';
import type Shape from '../objects/Shape';
import ToolSmartRecordType from '../tools/ToolSmartRecordType';
import ToolCreateSmartRecords from '../tools/ToolCreateSmartRecords';
import ToolShapeType from '../tools/ToolShapeType';
import terminology from '../utils/terminology';
import { type SmartRecordCreatableType, creatableRecordTypes } from '../types';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import {
  recordTypesToTypename,
  RECORD_ICONS,
  RECORD_ICON_BACKGROUND_COLORS,
} from '../utils/record';
import { SPACER_3 } from '../utils/style';
import { COLORS } from '../constants';
import type {
  EditorAlign,
  EditorVerticalAlign,
  TextProps,
} from '../objects/EmbeddedTextBehaviorsV2';
import { type StylusShapeOptions } from '../stylus/ShapeDrawingBrush';
import renderRecordName from '../components/aha_records/renderRecordName';
import epicCard from '../assets/icons/ahaRecords/epic.svg';
import featureCard from '../assets/icons/ahaRecords/feature.svg';
import ideaCard from '../assets/icons/ahaRecords/idea.svg';
import goalCard from '../assets/icons/ahaRecords/goal.svg';
import initiativeCard from '../assets/icons/ahaRecords/initiative.svg';
import releaseCard from '../assets/icons/ahaRecords/release.svg';
import requirementCard from '../assets/icons/ahaRecords/requirement.svg';
import { svgToIcon } from '../utils/svg';
import shapeSmartRecordIcon from '../assets/icons/shapeSmartRecordIcon.svg';
import { canConvertSmartRecords, isShapeSmartRecord } from '../objects/guards';

const MIN_SCALE = 1;
// Manual ordering from "smallest" record type to "largest"
const TYPE_ORDER = [
  'ideas',
  'features',
  'requirements',
  'epics',
  'releases',
  'initiatives',
  'goals',
];

// Try to use the lightest shade in the same color. For now, we don't have any
// clashes, but some record types do share a primary color (e.g., goals and
// strategic imperatives both use teal, but we don't support the latter right now).
// To determine the base color, look at src/utils/record.ts
const pillBackgroundColors: Record<SmartRecordCreatableType, string> = {
  epics: '#E5DCED', // purple-100
  features: '#DBEFFF', // blue-100
  goals: '#DDECF0', // teal-100
  ideas: '#E5F3D6', // green-100
  initiatives: '#F5DCE4', // pink-100
  releases: '#e1e1e1', // aha-gray-200, because aha-gray-100 is really light on a white background
  requirements: '#ffead1', // orange-100
};

const recordTypeToIcon = {
  epics: epicCard,
  features: featureCard,
  requirements: requirementCard,
  goals: goalCard,
  ideas: ideaCard,
  initiatives: initiativeCard,
  releases: releaseCard,
};

const makeSupportRecordTypes = supportedTypes => {
  return Object.fromEntries(
    Object.entries(recordTypesToTypename)
      .filter(([key]) => supportedTypes.includes(key))
      .sort((a, b) => TYPE_ORDER.indexOf(a[0]) - TYPE_ORDER.indexOf(b[0]))
  );
};

export const smartShapeMenuIcon = (recordType: SmartRecordCreatableType) => {
  const typename = ShapeSmartRecord.recordTypes[recordType];
  const icon = RECORD_ICONS[typename];
  if (recordType === 'epics') {
    return icon;
  }

  return icon.replace('solid', 'regular');
};

const TEXT_MARGIN_LEFT = SPACER_3;

class ShapeSmartRecord extends ShapeBase {
  recordNameMargin = DEFAULT_MARGIN;

  textProps: TextProps = {
    ...ShapeBase.defaultTextProps,
    margins: 'noScale',
    wrapText: 'wrapFixed',
    allowedStyles: 'simple',
  };

  constructor(fabricObject: Shape) {
    super(fabricObject);

    // Just a backup in case a smart record shape is somehow added without a feature
    // type. This is likely not necessary, but it doesn't hurt to have.
    if (!this.fabricObject.recordType) {
      this.fabricObject.recordType = 'features';
    }

    // Flag to tell the object to grow vertically as more lines of text are added
    fabricObject.wrapText = true;
    this.fabricObject.minScaleLimit = MIN_SCALE;

    this.fabricObject.enableFrameBehaviors();
  }

  static renderIcon() {
    return svgToIcon(shapeSmartRecordIcon);
  }

  static renderMenuIcon(options?: StylusShapeOptions): JSX.Element {
    return svgToIcon(recordTypeToIcon[options?.recordType ?? 'features']);
  }

  static defaultWidth() {
    return 200;
  }

  static defaultHeight() {
    return 75;
  }

  static get recordTypes() {
    let types = Object.keys(recordTypesToTypename).filter(
      type => creatableRecordTypes.includes(type) && type !== 'pdlcSteps'
    );

    if (
      window.currentAccount?.roadmapFlavor ||
      window.currentAccount?.onlyKnowledgeFlavor ||
      window.currentAccount?.onlyWhiteboardsFlavor
    ) {
      return makeSupportRecordTypes(types);
    }

    // Develop and ideas only accounts do not support these types.
    types = types.filter(
      type => !['goals', 'initiatives', 'releases'].includes(type)
    );

    if (window.currentAccount?.onlyTeamworkOrDevelopFlavor) {
      // Teamwork/Develop only accounts do not support ideas
      types = types.filter(type => type !== 'ideas');
    } else if (window.currentAccount?.onlyIdeasFlavor) {
      // Ideas only accounts do not support requirements
      types = types.filter(type => type !== 'requirements');
    }

    return makeSupportRecordTypes(types);
  }

  static terminologyFor(recordType: SmartRecordCreatableType) {
    return terminology(this.recordTypes[recordType]);
  }

  static getDescription(options: StylusShapeOptions): string {
    return this.recordTypes[options.recordType ?? 'features'];
  }

  get recordColor() {
    return RECORD_ICON_BACKGROUND_COLORS[this.recordTypename]
      .replace('var(', '')
      .replace(')', '');
  }

  get pillBackgroundColor() {
    return pillBackgroundColors[this.fabricObject.recordType ?? 'features'];
  }

  get recordTypename() {
    return ShapeSmartRecord.recordTypes[
      this.fabricObject.recordType ?? 'features'
    ];
  }

  get terminologyName() {
    const recordType = (this.fabricObject.recordType ??
      'features') as SmartRecordCreatableType;
    return ShapeSmartRecord.terminologyFor(recordType);
  }

  defaultTextContent() {
    const recordType = (this.fabricObject.recordType ??
      'features') as SmartRecordCreatableType;
    const terminology = ShapeSmartRecord.terminologyFor(recordType);
    const count =
      this.canvas
        ?.getObjects()
        .filter(isShapeSmartRecord)
        .filter(shape => shape.recordType === recordType).length ?? 1;
    return `${terminology} ${count}`;
  }

  getObjectProperties() {
    const textAlign: EditorAlign = 'left';
    const verticalAlign: EditorVerticalAlign = 'top';
    return {
      stroke: COLORS[this.recordColor],
      fill: '#FFFFFF',
      textAlign,
      verticalAlign,
      textColor: '#000000',
      fontSize: 14,
    };
  }

  tools() {
    const smartRecordsTools = [ToolSmartRecordType];

    if (
      canConvertSmartRecords() ||
      window.currentAccount?.hasCrossProductPromotionsEnabled
    ) {
      smartRecordsTools.push(ToolCreateSmartRecords);
    }

    return [[ToolShapeType], smartRecordsTools];
  }

  textTools() {
    return [];
  }

  calcTextMargin() {
    const defaultMargin = super.calcTextMargin();
    return {
      ...defaultMargin,
      top: 40,
      left: TEXT_MARGIN_LEFT,
    };
  }

  wasSet(keyOrPath: KeyPath, value: unknown): void {
    super.wasSet(keyOrPath, value);
    if (keyOrPath === 'recordType') {
      this.fabricObject.updateControlledProperties();
    }

    // There are two scenarios where we need to set the text content:
    // 1. When the shape is first created
    // 2. When a shape with no text content is converted to a smart record
    // In the first case, we need to do it after the canvas is set in order to
    // render the text properly
    if (keyOrPath === 'shapeName' || keyOrPath === 'canvas') {
      if (!this.fabricObject.textContent) {
        this.set('textContent', this.defaultTextContent());
      } else {
        // If there is text content, this will sanitize and re-render the text
        // content to strip out any styling
        this.fabricObject.setTextContent(this.fabricObject.plainText());
      }
    }

    if (keyOrPath === 'shapeName') {
      const compensatedScaleX =
        this.fabricObject.getScaledWidth() / ShapeSmartRecord.defaultWidth();
      const compensatedScaleY =
        this.fabricObject.getScaledHeight() / ShapeSmartRecord.defaultHeight();

      this.fabricObject.set({
        width: ShapeSmartRecord.defaultWidth(),
        height: ShapeSmartRecord.defaultHeight(),
      });

      this.set('scaleX', Math.max(MIN_SCALE, compensatedScaleX));
      this.set('scaleY', Math.max(MIN_SCALE, compensatedScaleY));
    }

    if (keyOrPath === 'textContent') {
      if (!value) {
        this.set('textContent', this.defaultTextContent());
      }
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const width = this.fabricObject.getScaledWidth();
    const height = this.fabricObject.getScaledHeight();
    const radius = 4;

    // Draw rounded rect with consistent rounded corners
    ctx.save();
    ctx.scale(1 / this.scaleX, 1 / this.scaleY);
    ctx.lineWidth = 1;
    ctx.strokeStyle = COLORS[this.recordColor];
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.moveTo(-width / 2 + radius, -height / 2);
    ctx.lineTo(width / 2 - radius, -height / 2);
    ctx.quadraticCurveTo(
      width / 2,
      -height / 2,
      width / 2,
      -height / 2 + radius
    );
    ctx.lineTo(width / 2, height / 2 - radius);
    ctx.quadraticCurveTo(width / 2, height / 2, width / 2 - radius, height / 2);
    ctx.lineTo(-width / 2 + radius, height / 2);
    ctx.quadraticCurveTo(
      -width / 2,
      height / 2,
      -width / 2,
      height / 2 - radius
    );
    ctx.lineTo(-width / 2, -height / 2 + radius);
    ctx.quadraticCurveTo(
      -width / 2,
      -height / 2,
      -width / 2 + radius,
      -height / 2
    );
    ctx.closePath();
    ctx.stroke();
    ctx.fill();

    ctx.restore();

    // Render pill inside smart record
    renderRecordName(ctx, this);
  }
}

export default ShapeSmartRecord;
